/**
 * DealInspector — click a Real value in the forecast table → popover lists
 * the HubSpot deals contributing to it.
 *
 * Marco's ask after the €1.014-in-June ghost-deal episode: we couldn't
 * tell which deal was driving a Real cell's value without paste-into-
 * console gymnastics. This component surfaces the breakdown inline.
 *
 * Renders:
 *   - Nothing if the deals array is empty (so the cell stays clean).
 *   - A small ⓘ icon button next to the Real value otherwise.
 *   - On click: popover with a sorted list of deals (largest first),
 *     each row: name + company + amount + close-date + HubSpot ↗ link.
 *
 * Self-positioning: opens above or below the trigger depending on
 * available room (the deals table renders deep in the page, so cells
 * near the bottom would clip if the popover always opened below).
 *
 * Closes on: outside click, Escape, or clicking a link (link opens in
 * a new tab so the user comes back to the same dashboard state).
 */
import React from "react";

export interface DealRecord {
  id: string;
  name?: string | null;
  company?: string | null;
  amount?: number;
  closeDate?: string | null;
  ownerId?: string | null;
  cell?: string;
  monthKey?: string;
}

/** Optional breakdown row — surfaces the per-component math for a composite
 *  cell. Used for Real Add so the user can see exactly which sub-totals (GTM
 *  HubSpot, Partner HubSpot, …, Manual AE actuals) sum to the cell value.
 *  When `manual: true`, the row represents non-HubSpot data (e.g. per-AE
 *  numbers the user typed into the forecast table). The popover renders a
 *  badge so the difference is visually obvious. */
export interface DealInspectorBreakdownRow {
  label: string;
  amount: number;
  dealCount?: number;
  manual?: boolean;
}

interface DealInspectorProps {
  /** The deals contributing to this cell — already filtered by caller. */
  deals: DealRecord[];
  /** HubSpot portal id — used to build the deep-link URL. */
  portalId: string | number | null;
  /** Owners map: { ownerId: { name } } — for the "owner" column in the list. */
  ownersMap?: Record<string, { name?: string }>;
  /** Localised label. Drives the "Deals contributing" header. */
  lang: "it" | "en";
  /** Money formatter (eur / fmtMoney). Receives a raw number. */
  fmtMoney: (n: number) => string;
  /** Optional cell name (e.g. "gtmActual") shown as a sub-caption in the
   *  popover header for context. */
  cellLabel?: string;
  /** Optional component breakdown — rendered as a small table above the
   *  deal list. Use for composite cells like Real Add where the cell value
   *  is the sum of several HubSpot subtotals plus manual entries that have
   *  no deal records. When omitted, only the deal list is rendered. */
  breakdown?: DealInspectorBreakdownRow[];
  /** Optional explicit total. When provided, the header shows this instead
   *  of the sum of deal amounts. Useful when the cell value includes manual
   *  components (aeTotalReal) that aren't in the deals array. */
  total?: number;
}

function hubspotUrl(portalId: string | number | null, dealId: string): string | null {
  if (!portalId || !dealId) return null;
  // Cloudflare deploys are EU region; Marco's portal is on app-eu1.
  // app.hubspot.com transparently redirects to the right region, so the
  // generic domain is safer than hardcoding app-eu1.
  return `https://app.hubspot.com/contacts/${portalId}/record/0-3/${dealId}`;
}

export default function DealInspector({
  deals,
  portalId,
  ownersMap,
  lang,
  fmtMoney,
  cellLabel,
  breakdown,
  total: totalOverride,
}: DealInspectorProps) {
  const [open, setOpen] = React.useState(false);
  const [placement, setPlacement] = React.useState<"above" | "below">("below");
  const wrapRef = React.useRef<HTMLSpanElement>(null);
  const triggerRef = React.useRef<HTMLButtonElement>(null);

  // Don't render the trigger if there's nothing to show. We render whenever
  // there's at least one deal OR at least one non-zero breakdown row — the
  // latter case covers Real Add cells that have €0 of HubSpot deals but a
  // manual aeTotalReal contribution.
  const hasBreakdownData = Array.isArray(breakdown) && breakdown.some(b => Math.abs(b.amount) > 0);
  if ((!deals || deals.length === 0) && !hasBreakdownData) return null;

  // Sort biggest first so the contributing-most deal is at the top.
  const sorted = React.useMemo(() => {
    return [...(deals || [])].sort((a, b) => (b.amount || 0) - (a.amount || 0));
  }, [deals]);
  const dealsSum = sorted.reduce((s, d) => s + (d.amount || 0), 0);
  // Header total: explicit override (e.g. Real Add includes aeTotalReal which
  // isn't in `deals`) wins; otherwise fall back to the sum of deal amounts.
  const total = typeof totalOverride === "number" ? totalOverride : dealsSum;

  // Outside-click + Escape close.
  React.useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  // Decide placement when opening: if the trigger is in the bottom half
  // of the viewport, open above so the popover doesn't clip below the fold.
  // Threshold matches the popover's compact max-height (list capped at 180px
  // + breakdown ~140px max + header/footer ~80px ≈ 220px worst-case).
  const handleToggle = React.useCallback(() => {
    if (!open && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      const room = window.innerHeight - rect.bottom;
      setPlacement(room < 240 ? "above" : "below");
    }
    setOpen(o => !o);
  }, [open]);

  const dealsCount = sorted.length;
  const dealsWord = lang === "it"
    ? "deal" // it: "deal" is invariant in modern usage
    : (dealsCount === 1 ? "deal" : "deals");
  const headerText = `${dealsCount} ${dealsWord} · ${fmtMoney(total)}`;

  return (
    <span ref={wrapRef} className="deal-inspector">
      <button
        ref={triggerRef}
        type="button"
        className={`deal-inspector__trigger ${open ? "deal-inspector__trigger--open" : ""}`}
        onClick={handleToggle}
        title={lang === "it"
          ? `Mostra deal contribuenti (${dealsCount})`
          : `Show contributing deals (${dealsCount})`}
        aria-haspopup="dialog"
        aria-expanded={open}
      >
        <svg width="12" height="12" viewBox="0 0 16 16" fill="none" aria-hidden="true">
          <circle cx="8" cy="8" r="6.5" stroke="currentColor" strokeWidth="1.4" />
          <circle cx="8" cy="5.2" r="0.9" fill="currentColor" />
          <path d="M7.4 7.5 H8.6 V11" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
        </svg>
      </button>
      {open && (
        <div
          className={`deal-inspector__panel deal-inspector__panel--${placement}`}
          role="dialog"
        >
          <div className="deal-inspector__header">
            <span className="deal-inspector__header-text">{headerText}</span>
            {cellLabel && <span className="deal-inspector__header-cell">{cellLabel}</span>}
          </div>
          {hasBreakdownData && (
            // Breakdown table — surfaces the per-component math for composite
            // cells (Real Add = GTM + Partner + Self + Upsell + Other AEs +
            // manual per-AE entries). Manual rows get a "manual" badge so
            // it's obvious why they can't be drilled into via HubSpot.
            <div className="deal-inspector__breakdown">
              <div className="deal-inspector__breakdown-head">
                {lang === "it" ? "Composizione" : "Breakdown"}
              </div>
              <ul className="deal-inspector__breakdown-list">
                {breakdown!.filter(b => Math.abs(b.amount) > 0).map((b, i) => (
                  <li key={`${b.label}-${i}`} className="deal-inspector__breakdown-row">
                    <span className="deal-inspector__breakdown-label">
                      {b.label}
                      {b.manual && (
                        <span className="deal-inspector__breakdown-badge" title={lang === "it"
                          ? "Inserito manualmente in dashboard — non è un deal HubSpot"
                          : "Manually entered in the dashboard — not a HubSpot deal"}>
                          {lang === "it" ? "manuale" : "manual"}
                        </span>
                      )}
                      {!b.manual && typeof b.dealCount === "number" && (
                        <span className="deal-inspector__breakdown-count">
                          {" "}· {b.dealCount} {b.dealCount === 1 && lang === "en" ? "deal" : (lang === "it" ? "deal" : "deals")}
                        </span>
                      )}
                    </span>
                    <span className="deal-inspector__breakdown-amount">{fmtMoney(b.amount)}</span>
                  </li>
                ))}
                <li className="deal-inspector__breakdown-row deal-inspector__breakdown-row--total">
                  <span className="deal-inspector__breakdown-label">
                    {lang === "it" ? "Totale" : "Total"}
                  </span>
                  <span className="deal-inspector__breakdown-amount">{fmtMoney(total)}</span>
                </li>
              </ul>
            </div>
          )}
          {sorted.length > 0 && hasBreakdownData && (
            <div className="deal-inspector__sub-head">
              {lang === "it" ? "Deal contribuenti" : "Contributing deals"}
            </div>
          )}
          <ul className="deal-inspector__list">
            {sorted.map(d => {
              const url = hubspotUrl(portalId, d.id);
              const ownerName = (d.ownerId && ownersMap?.[d.ownerId]?.name) || null;
              const dateStr = d.closeDate ? String(d.closeDate).slice(0, 10) : "—";
              return (
                <li key={d.id} className="deal-inspector__item">
                  <div className="deal-inspector__item-main">
                    <span className="deal-inspector__item-name" title={d.name || ""}>
                      {d.name || (lang === "it" ? "(senza nome)" : "(no name)")}
                    </span>
                    {d.company && (
                      <span className="deal-inspector__item-company"> · {d.company}</span>
                    )}
                  </div>
                  <div className="deal-inspector__item-meta">
                    <span className="deal-inspector__item-amount">{fmtMoney(d.amount || 0)}</span>
                    <span className="deal-inspector__item-sep">·</span>
                    <span className="deal-inspector__item-date">{dateStr}</span>
                    {ownerName && (
                      <>
                        <span className="deal-inspector__item-sep">·</span>
                        <span className="deal-inspector__item-owner">{ownerName}</span>
                      </>
                    )}
                    {url && (
                      <a
                        href={url}
                        target="_blank"
                        rel="noreferrer"
                        className="deal-inspector__item-link"
                        onClick={(e) => e.stopPropagation()}
                        title={lang === "it" ? "Apri su HubSpot" : "Open in HubSpot"}
                      >
                        ↗
                      </a>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
          <div className="deal-inspector__footer">
            {lang === "it"
              ? "I valori vengono da HubSpot via Worker — modifica il deal in HubSpot per cambiare il totale."
              : "Values come from HubSpot via the Worker — edit the deal in HubSpot to change the total."}
          </div>
        </div>
      )}
    </span>
  );
}
